import { useEffect, useMemo, FC, useCallback, useContext, useRef } from 'react';
import {
  chain,
  slice,
  includes,
  head,
  last,
  isEmpty,
  findIndex,
  matches,
  nth,
  get,
  map,
  filter,
  forEach,
  max,
  min,
  reverse,
  range,
  concat,
  find,
  isEqual,
  isFunction,
  isNil,
  noop,
  reduce,
  constant,
  toNumber,
  isNumber,
  isString,
  uniqBy,
  identity,
} from 'lodash';
import {
  DATA_FORMATTER,
  DATA_TYPE_FORMAT,
  hasDashletSuffix,
  VIZ_OPTION_IDS,
  VIZ_QUERY_TOTALS_FLAG,
  whiteStripesFill,
} from '../../../common';
import {
  MetricAggregateType,
  IPipelineChangesDataAnnotated,
  IPipelineChangesColumnData,
  D3StrategyHookType,
  DrillLinkingProps,
  INoDataHocProps,
  UseMouseEventsProps,
  UseRendererProps,
} from './pipeline-changes.interfaces';
import { IAnyAttribute, IPreFilterOperand } from '../../../datasets';
import {
  useCustomFormatToggleSelector,
  useOpenDiscoveryPresentStateSelector,
  useOpenVizSelector,
  useReportLinkMaybeSideDrawer,
  useVizOptionSelector,
} from '../../../common/redux/selectors/viz-selector.hook';
import { useAccount } from '../../../common/utilities/account';
import { ILegendDatum, VizLegendUtils } from '../viz-legend';
import { LegendShapes } from '../legend-shape';
import {
  centeredPanel,
  useDiscoverTheme,
  useDiscoverThemeColors,
} from '../../../common/emotion';
import ColorManager from '../../../common/d3/ColorManager';
import { useDispatch, useSelector } from 'react-redux';
import Discover from '../../../common/redux/actions/DiscoverActions';
import { messages } from '../../../i18n';
import { Viz } from '../../VizUtil';
import { useHasValueChanged } from '../../../common/utilities/state-helpers.hook';
import { ILinkToReport, IDrillContext } from '../../viz-redirect';
import { useReportLinkEnabled } from '../base-cartesian-chart';
import { useIsDashletMode } from '../../../auth';
import { useHistory } from '../../../common/utilities/with-router';
import { IToggle } from '../../interfaces';
import { TOOLTIP_ID } from '../chart-tooltip/chart-tooltip.hook';
import { FormattedNameValue } from '../chart-tooltip/chart-tooltip.interface';
import { ChartContext } from '../../viz-chart/chart.hook';
import {
  axisBottom,
  axisLeft,
  color,
  max as d3Max,
  min as d3Min,
  pointer,
  scaleBand,
  scaleLinear,
  select,
} from 'd3';
import { topRoundedRect } from '../../../common/d3/utils/bars';
import {
  getGradientId,
  getAdjustedColors,
} from '../../../common/d3/linear-gradient';
import JSON5 from 'json5';
import { useFiscalCalendarInfo } from '../../../common/utilities/fiscal-info.hook';

// @NOTE: these definitions are hard-coded dependencies based on DSC-6171 viz. Should be dynamic sometime later
export const CLOSED_WON_FIELD_NAME = 'Closed Won';
export const pipelineChangesStageOrder = [
  'Start',
  'New',
  'Added',
  'Increases',
  'Moved In',
  'Moved Out',
  'Decreases',
  'Removed',
  'Closed Lost',
  CLOSED_WON_FIELD_NAME,
  'End',
];

export const allUpsideStages = slice(pipelineChangesStageOrder, 1, 4);
export const allDownsideStages = slice(pipelineChangesStageOrder, 5, 9);

// These labels should be eventually translated
export const LegendKeys = {
  StartEnd: 'Pipeline',
  Increases: 'Increases',
  Decreases: 'Decreases',
  ClosedWon: CLOSED_WON_FIELD_NAME,
};

export const filterByZeroValue = ({ value = 0 }) => {
  return Math.abs(value) === 0;
};

export const getFocusedLabel = (
  stageName,
  upsideStages = [],
  downsideStages = [],
  stageNames = [],
) => {
  if (includes([head(stageNames), last(stageNames)], stageName)) {
    return LegendKeys.StartEnd;
  } else if (stageName === CLOSED_WON_FIELD_NAME) {
    return LegendKeys.ClosedWon;
  } else if (includes(upsideStages, stageName)) {
    return LegendKeys.Increases;
  } else if (includes(downsideStages, stageName)) {
    return LegendKeys.Decreases;
  }
  return LegendKeys.StartEnd;
};

export const isAdditionalBar = label =>
  includes(
    [
      messages.chartSpecs.waterfall.extra,
      messages.chartSpecs.waterfall.missing,
    ],
    label,
  );

export const useForecastChangesColors = (vizId: string) => {
  const dispatch = useDispatch();
  const customColors = useVizOptionSelector({
    discoveryId: vizId,
    option: VIZ_OPTION_IDS.customColors,
  });
  const {
    PipelineChangesForecastColor: ForecastColor,
    PipelineChangesIncreasesColor: IncreasesColor,
    PipelineChangesDecreasesColor: DecreasesColor,
    PipelineChangesClosedWonColor: ClosedWonColor,
  } = useDiscoverThemeColors();

  const defaultColors = useMemo(
    () => ({
      increasesColor: IncreasesColor,
      decreasesColor: DecreasesColor,
      closedWonColor: ClosedWonColor,
      startEndColor: ForecastColor,
    }),
    [ForecastColor, IncreasesColor, DecreasesColor, ClosedWonColor],
  );

  const { increasesColor, decreasesColor, closedWonColor, startEndColor } =
    defaultColors;

  // Forecast Changes has a default color set
  useEffect(() => {
    const colorSet = [
      { [LegendKeys.Increases]: IncreasesColor },
      { [LegendKeys.Decreases]: DecreasesColor },
      { [LegendKeys.ClosedWon]: ClosedWonColor },
      { [LegendKeys.StartEnd]: ForecastColor },
    ];

    ColorManager.addGroup(
      vizId,
      true,
      isEmpty(customColors)
        ? colorSet
        : colorSet.filter(colorObj => {
            const colorKey = Object.keys(colorObj)[0];
            return !customColors[colorKey];
          }),
    );
  }, [
    dispatch,
    vizId,
    customColors,
    IncreasesColor,
    DecreasesColor,
    ClosedWonColor,
    ForecastColor,
  ]);

  return useMemo(() => {
    if (!isEmpty(customColors)) {
      return {
        increasesColor: customColors[LegendKeys.Increases]
          ? ColorManager.getColor(vizId, LegendKeys.Increases)
          : IncreasesColor,
        decreasesColor: customColors[LegendKeys.Decreases]
          ? ColorManager.getColor(vizId, LegendKeys.Decreases)
          : DecreasesColor,
        closedWonColor: customColors[LegendKeys.ClosedWon]
          ? ColorManager.getColor(vizId, LegendKeys.ClosedWon)
          : ClosedWonColor,
        startEndColor: customColors[LegendKeys.StartEnd]
          ? ColorManager.getColor(vizId, LegendKeys.StartEnd)
          : ForecastColor,
      };
    }
    return {
      increasesColor,
      decreasesColor,
      closedWonColor,
      startEndColor,
    };
  }, [
    customColors,
    increasesColor,
    decreasesColor,
    closedWonColor,
    startEndColor,
    vizId,
    IncreasesColor,
    DecreasesColor,
    ClosedWonColor,
    ForecastColor,
  ]);
};

export const useSegmentStages = (
  segmentFieldName: string,
  columnNames: string[],
  results: any[] = [],
  stageNameLabelChangeDict: { [key: string]: string } = {},
) => {
  return useMemo(() => {
    const stageNameIdx = findIndex(columnNames, matches(segmentFieldName));
    if (stageNameIdx < 0) {
      return [];
    }

    return chain(results)
      .map((_resultRow: any[]) => {
        const _stageName = nth(_resultRow, stageNameIdx) as string;
        return get(stageNameLabelChangeDict, _stageName, _stageName);
      })
      .reject(matches(VIZ_QUERY_TOTALS_FLAG))
      .reject(isEmpty)
      .uniq()
      .value();
  }, [segmentFieldName, columnNames, results, stageNameLabelChangeDict]);
};

export const useMetricValuesSortedByStage = (
  stageNames: string[],
  results: any[],
  metricField,
  metricFormatter,
  metricIndex = 0,
): MetricAggregateType[] => {
  return useMemo<MetricAggregateType[]>(() => {
    return map(stageNames, (stage: string): MetricAggregateType => {
      // VIZ_QUERY_TOTALS_FLAG indicates the total'd data for the metrics
      const aggregateRows = filter(
        results,
        resultRow =>
          includes(resultRow, stage) &&
          includes(resultRow, VIZ_QUERY_TOTALS_FLAG),
      );

      // stage may not exist in query results. Default metric value for that stage to zero
      const aggregateRow = head(aggregateRows);
      const metricValue = aggregateRow ? nth(aggregateRow, metricIndex) : 0;

      return {
        stage,
        stageLabel: stage,
        metricName: metricField?.name,
        metricValue,
        metricValueFormatter: metricFormatter,
      } as MetricAggregateType;
    });
  }, [metricField, metricFormatter, metricIndex, stageNames, results]);
};

export const useMetricsWithStageAggregation = (
  stageNames: string[],
  metricFields: IAnyAttribute[],
  columnInfo: any[],
  results: any[],
): MetricAggregateType[][] => {
  const metricNamesWithIndexes = map(metricFields, field => {
    const _fieldIndex = findIndex(columnInfo, {
      attributeName: field?.name,
    });

    const fieldFormatter =
      DATA_TYPE_FORMAT.getFormatterByName(field?.formatType) ||
      DATA_TYPE_FORMAT.getDefaultFormatterForType(field?.attributeType);

    return {
      metricField: field,
      index: _fieldIndex,
      metricFormatter: fieldFormatter,
    };
  });

  return map(
    metricNamesWithIndexes,
    ({ metricField, index: _metricIndexInQueryResults, metricFormatter }) => {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return useMetricValuesSortedByStage(
        stageNames,
        results,
        metricField,
        metricFormatter,
        _metricIndexInQueryResults,
      );
    },
  );
};
export const useAggregatedMetricsWithTimeLabels = (
  metricsWithStageAggregation: MetricAggregateType[][],
  columnInfo: any[],
  results: any[],
  currentSnapshotField: IAnyAttribute,
  previousSnapshotField?: IAnyAttribute,
): MetricAggregateType[][] => {
  return useMemo<MetricAggregateType[][]>(() => {
    const currentSnapshotFieldOrdinal = findIndex(columnInfo, {
      attributeName: currentSnapshotField?.name,
    });
    if (
      isEmpty(columnInfo) ||
      isEmpty(previousSnapshotField) ||
      currentSnapshotFieldOrdinal < 0
    ) {
      return metricsWithStageAggregation;
    }
    const previousSnapshotFieldOrdinal = findIndex(columnInfo, {
      attributeName: previousSnapshotField.name,
    });
    const nonAggregateResultRow = find(results, row => {
      return (
        row[currentSnapshotFieldOrdinal] !== VIZ_QUERY_TOTALS_FLAG &&
        row[previousSnapshotFieldOrdinal] !== VIZ_QUERY_TOTALS_FLAG
      );
    });

    if (isEmpty(nonAggregateResultRow)) {
      return metricsWithStageAggregation;
    } else {
      const startLabel = nonAggregateResultRow[previousSnapshotFieldOrdinal];
      const endLabel = nonAggregateResultRow[currentSnapshotFieldOrdinal];

      return map(metricsWithStageAggregation, metricRow => {
        return map(metricRow, obj => {
          if (obj.stage === head(metricRow)?.stage) {
            return {
              ...obj,
              stageLabel: startLabel,
            };
          } else if (obj.stage === last(metricRow)?.stage) {
            return {
              ...obj,
              stageLabel: endLabel,
            };
          } else {
            return {
              ...obj,
            };
          }
        });
      });
    }
  }, [
    columnInfo,
    currentSnapshotField,
    previousSnapshotField,
    results,
    metricsWithStageAggregation,
  ]);
};

/**
 * build the data for d3 to consume
 * - calculates absolute and relative amounts for the eventual size/position of bars
 * - label
 * - closed won flag (special color flag)
 * - formatter (for eventual render)
 */
export const useAnnotatedData = (
  queryError: any,
  upsideStages: string[],
  orderedStageAggregations: MetricAggregateType[],
): IPipelineChangesDataAnnotated[] => {
  return useMemo<IPipelineChangesDataAnnotated[]>(() => {
    if (queryError) {
      return [];
    }

    let sumAfterCurrentValue = 0;
    const _pipelineChangesDataAnnotated: IPipelineChangesDataAnnotated[] = [];
    forEach(orderedStageAggregations, (_stageAggregation, _stageIdx) => {
      const {
        stage: stageName,
        stageLabel,
        metricValue: stageMetricAggregate,
        metricName: stageMetricName,
        metricValueFormatter,
      } = _stageAggregation;

      // Each stage is a relative change from the last stage. Upside changes are positive, downside stages are negative
      const effectivePipelineStageValue: number = includes(
        upsideStages,
        stageName,
      )
        ? Math.abs(stageMetricAggregate)
        : -1 * Math.abs(stageMetricAggregate);

      if (_stageIdx === 0) {
        sumAfterCurrentValue = effectivePipelineStageValue;
      } else {
        sumAfterCurrentValue =
          _pipelineChangesDataAnnotated[_stageIdx - 1].sumAfterCurrentValue +
          effectivePipelineStageValue;
      }

      _pipelineChangesDataAnnotated[_stageIdx] = {
        stage: stageName,
        label: stageLabel,
        value:
          0 === Math.abs(effectivePipelineStageValue)
            ? 0
            : effectivePipelineStageValue,
        primaryMetricName: stageMetricName,
        sumAfterCurrentValue,
        formatter: metricValueFormatter ?? DATA_FORMATTER.WHOLE_NUMBER,
      };
    });

    const pipelineValuesSum =
      last(_pipelineChangesDataAnnotated)?.sumAfterCurrentValue ?? 0;

    // if the pipeline is not balanced, add a special bar to indicate the difference
    if (Math.floor(Math.abs(pipelineValuesSum)) !== 0) {
      _pipelineChangesDataAnnotated.push({
        ...last(_pipelineChangesDataAnnotated),
        label:
          pipelineValuesSum > 0
            ? messages.chartSpecs.waterfall.missing
            : messages.chartSpecs.waterfall.extra,
        sumAfterCurrentValue: Math.abs(pipelineValuesSum),
        value: Math.abs(pipelineValuesSum),
      });
    }

    return _pipelineChangesDataAnnotated;
  }, [queryError, upsideStages, orderedStageAggregations]);
};

export const COLLAPSING_PERCENTAGE_THRESHOLD = 1 / 3;

/**
 * useScaleFeatures calculates and provides anything related to the scale of the chart
 * @param stageNames
 * @param chartXPadding
 * @param chartYPadding
 * @param pipelineChangesData
 * @param chartWidth
 * @param chartHeight
 */
export const useScaleFeatures = ({
  vizId,
  stageLabels = [],
  chartXPadding,
  chartYPadding,
  pipelineChangesData,
}: {
  vizId: string;
  stageLabels: string[];
  chartXPadding: number;
  chartYPadding: number;
  pipelineChangesData: IPipelineChangesDataAnnotated[];
}) => {
  const { height: chartHeight, width: chartWidth } = useContext(ChartContext);
  const canCollapseToggle: IToggle = useCustomFormatToggleSelector({
    discoveryId: vizId,
    toggleName: 'canCollapse',
    defaultValue: false,
  });

  const canCollapse = !!canCollapseToggle?.on;

  return useMemo(() => {
    // quantitative domain for d3
    const chartMinValue = d3Min(
      pipelineChangesData,
      _pipelineChangesDatum => _pipelineChangesDatum.sumAfterCurrentValue,
    );
    const chartMaxValue = d3Max(
      pipelineChangesData,
      _pipelineChangesDatum => _pipelineChangesDatum.sumAfterCurrentValue,
    );

    // scaling functions for d3
    const xScale = scaleBand()
      .domain(stageLabels)
      .range([chartXPadding, chartWidth - chartXPadding]);

    // collapse whitespace if necessary
    const bodyValues = map(
      slice(pipelineChangesData, 1, pipelineChangesData.length - 1),
      'value',
    );
    const maxBodyValue = max(map(bodyValues, Math.abs));
    const endBarCrossesZero = head(
      pipelineChangesData.filter(
        ({ sumAfterCurrentValue }) => sumAfterCurrentValue < 0,
      ),
    );

    const newAndEndValues = map(
      isNil(endBarCrossesZero)
        ? [head(pipelineChangesData), last(pipelineChangesData)]
        : [
            head(pipelineChangesData),
            last(pipelineChangesData),
            {
              value:
                Math.abs(endBarCrossesZero.value) -
                Math.abs(endBarCrossesZero.sumAfterCurrentValue),
            },
          ],
      'value',
    );

    // using the minimum makes sure that collapsed data stays above above the axis
    const newEndAbsoluteValues = map(newAndEndValues, Math.abs);
    const minNewEnd = min(newEndAbsoluteValues);
    const minPercentOfNewEnd = maxBodyValue <= 0 ? 0 : minNewEnd / maxBodyValue;
    const isCollapsing =
      canCollapse && minPercentOfNewEnd >= COLLAPSING_PERCENTAGE_THRESHOLD;
    const fullBodyValue = chartMaxValue - minNewEnd;

    // if collapsing the chart, make the base "zero-value" relative to the size of stages between new/end
    const chartFloor = isCollapsing
      ? minNewEnd - fullBodyValue * COLLAPSING_PERCENTAGE_THRESHOLD
      : 0;

    // chart boundaries - based on min/max, but get a 'rounded' padding above the max
    // NOTE: current data structure doesn't lend well to d3.extent
    const adjustedChartMax = Math.abs(chartMaxValue - chartFloor);
    const log10 = adjustedChartMax > 0.001 ? Math.log10(chartMaxValue) : 0;
    const log10Floor = Math.floor(log10);
    const minSteps = max([5, log10Floor]);
    const numSteps = min([minSteps, 10]);
    const stepValue = (adjustedChartMax * 1.08) / numSteps;

    const yScale = scaleLinear()
      .domain([chartFloor, chartMaxValue])
      .range([
        chartHeight - chartYPadding,
        chartYPadding + chartYPadding / 4, // adding 1/4 prevents some labels from being cut off
      ]);

    const [yDomStart, yDomEnd] = yScale.domain();

    let yTickValues = reverse(
      range(yDomStart, yDomEnd + stepValue, stepValue) as any,
    );

    // unlikely for negative values, but guards for it
    if (chartMinValue < 0) {
      const negativeRangedTicks = range(yDomStart, chartMinValue, -stepValue);
      yTickValues = concat(yTickValues, negativeRangedTicks);
    }

    // bandWidth the maximum width a bar can be without overlapping into the adjacent bar
    const bandWidth = scaleBand()
      .domain(stageLabels)
      .range([chartXPadding, chartWidth - chartXPadding])
      .bandwidth();
    const barWidth = bandWidth / 1.1;

    // xScaleDomainSelector ensures that d3 has a unique way to map data to the x axis
    const xScaleDomainSelector = data => data?.label;

    return {
      xScale,
      xScaleDomainSelector,
      yScale,
      yTickValues,
      barWidth,
      bandWidth,
    };
  }, [
    pipelineChangesData,
    stageLabels,
    chartXPadding,
    chartWidth,
    canCollapse,
    chartHeight,
    chartYPadding,
  ]);
};

export const useBarColor = ({ vizId, pipelineChangesData }) => {
  const { increasesColor, decreasesColor, closedWonColor, startEndColor } =
    useForecastChangesColors(vizId);

  const getBarColor = useCallback(
    stageName => {
      const datum = find(pipelineChangesData, { stage: stageName });
      const idx = findIndex(pipelineChangesData, { stage: stageName });

      const isEndColumn = idx === 0 || idx === pipelineChangesData.length - 1;
      const isOffsetNegative = datum.value < 0;
      const offsetColor = isOffsetNegative ? decreasesColor : increasesColor;
      const isClosedWon = stageName === CLOSED_WON_FIELD_NAME;

      return isEndColumn
        ? startEndColor
        : isClosedWon
          ? closedWonColor
          : offsetColor;
    },
    [
      closedWonColor,
      decreasesColor,
      increasesColor,
      pipelineChangesData,
      startEndColor,
    ],
  );

  return {
    getBarColor,
  };
};

/**
 * useD3DataBuilder calculates the finalized positions/numbers/strings for d3 elements
 * @param vizId
 * @param pipelineChangesData
 * @param yScale
 * @param metricAggregationsByStage
 * @param xScale
 * @param xScaleDomainSelector
 * @param barWidth
 * @param maximumBarWidth
 * @param upsideStages
 * @param downsideStages
 * @param stageNames
 */
export const useD3DataBuilder = ({
  vizId,
  pipelineChangesData,
  yScale,
  metricAggregationsByStage,
  xScale,
  xScaleDomainSelector,
  barWidth,
  maximumBarWidth,
  downsideStages,
  isDataFocused,
  getBarColor,
  showPositiveLabels = false,
  getFieldByStageName = () => ({}) as IAnyAttribute,
  additionalCalcs,
  additionalMetrics,
  toShelves,
}: D3StrategyHookType) => {
  const {
    colors: { WaterfallExtraBar },
  } = useDiscoverTheme();
  const { currentUser: { i18nPrefs = {} } = {} } = useAccount();
  const viz = useOpenVizSelector({ discoveryId: vizId });
  const linkToReport: ILinkToReport | object = useVizOptionSelector({
    discoveryId: vizId,
    option: VIZ_OPTION_IDS.linkToReport,
    defaultValue: {},
  });

  const filters = useVizOptionSelector({
    discoveryId: vizId,
    option: VIZ_OPTION_IDS.filters,
  });

  return useMemo(() => {
    const pipelineChangesColumnData: IPipelineChangesColumnData[] = map(
      pipelineChangesData,
      (datum: IPipelineChangesDataAnnotated, idx) => {
        const {
          value,
          sumAfterCurrentValue,
          stage: stageName,
          primaryMetricName,
          label,
        } = datum;
        const customFormatProps =
          Viz.getDataCustomFormatters(viz)?.[primaryMetricName];
        const previousSum =
          idx === 0 ? 0 : pipelineChangesData[idx - 1]?.sumAfterCurrentValue;
        const isPreviousBelowZero = previousSum < 0;
        const isCurrentBelowZero = previousSum > 0 && sumAfterCurrentValue < 0;

        const isAdditional = isAdditionalBar(label);

        // the chart can collapse whitespace, based on size of start/end of data
        const scaledBase =
          idx === 0 ||
          idx === pipelineChangesData.length - 1 ||
          isCurrentBelowZero
            ? head(yScale.range())
            : yScale(0);

        const barLength =
          scaledBase -
          yScale(
            Math.abs(sumAfterCurrentValue < 0 ? max([previousSum, 0]) : value),
          );
        const barPosLeft: number =
          xScale(xScaleDomainSelector(datum)) +
          (maximumBarWidth - barWidth) / 2;
        const barPosTop: number = isPreviousBelowZero
          ? head(yScale.range())
          : yScale(max([previousSum, sumAfterCurrentValue]));

        const metricAggregationsForCurrentStage: MetricAggregateType[] = get(
          metricAggregationsByStage,
          stageName,
          [],
        );

        const valueAboveZero = isCurrentBelowZero ? previousSum : value;
        const barValue = isPreviousBelowZero ? 0 : valueAboveZero;

        const formattedMetricAggregations: FormattedNameValue[] = map(
          metricAggregationsForCurrentStage,
          ({
            stage,
            metricName: _metricName,
            metricValue,
            metricValueFormatter,
          }) => {
            const metricValueAboveZero = isCurrentBelowZero
              ? previousSum
              : metricValue;
            const adjustedMetricValue: number = isPreviousBelowZero
              ? 0
              : metricValueAboveZero;
            const adjustedStageValue: number =
              isEqual(_metricName, primaryMetricName) &&
              includes(downsideStages, stage) &&
              !showPositiveLabels
                ? -1 * Math.abs(adjustedMetricValue)
                : Math.abs(adjustedMetricValue);
            const formattedValue: string =
              metricValueFormatter?.formatSmall(
                adjustedStageValue,
                i18nPrefs,
              ) ?? `${adjustedStageValue}`;
            return {
              name: _metricName,
              value: formattedValue,
            };
          },
        );

        const additionalBarTooltipData: FormattedNameValue[] = [
          {
            name: label,
            value:
              head(
                metricAggregationsForCurrentStage,
              )?.metricValueFormatter?.formatSmall(
                Math.abs(value),
                i18nPrefs,
              ) ?? `${Math.abs(value)}`,
          },
        ];

        const textLabel = Viz.formatNumberToFit(
          showPositiveLabels ? Math.abs(barValue) : barValue,
          barWidth - 20,
          datum.formatter,
          i18nPrefs,
          customFormatProps,
        );

        const fieldToFilter = getFieldByStageName(stageName);

        const attributes: IPreFilterOperand[] = [
          {
            attribute: fieldToFilter,
            value: [stageName],
          },
        ];

        const drillContext: IDrillContext = {
          linkToReport: linkToReport as ILinkToReport,
          attributes,
          filters,
          calcs: additionalCalcs,
          metrics: additionalMetrics,
          toShelves,
        };

        const d3Datum: IPipelineChangesColumnData = {
          x: barPosLeft,
          y: isAdditional ? yScale(Math.abs(value)) : barPosTop,
          width: barWidth,
          height: barLength,
          labelPosX: barPosLeft + barWidth / 2,
          adjacentConnectorLeft: barPosLeft + barWidth,
          adjacentConnectorRight: barPosLeft + maximumBarWidth, // algebraically, this is the reduced formula
          fill: isAdditional ? WaterfallExtraBar : getBarColor(stageName),
          value,
          label: isAdditional ? '' : textLabel,
          stageName,
          ordinal: idx,
          tooltipData: isAdditional
            ? additionalBarTooltipData
            : formattedMetricAggregations,
          drillContext,
          isAdditionalBar: isAdditional,
        };

        d3Datum.dim = isAdditional ? false : !isDataFocused(d3Datum);

        return d3Datum;
      },
    );

    return pipelineChangesColumnData;
  }, [
    pipelineChangesData,
    viz,
    yScale,
    xScale,
    xScaleDomainSelector,
    maximumBarWidth,
    barWidth,
    metricAggregationsByStage,
    i18nPrefs,
    getFieldByStageName,
    linkToReport,
    filters,
    additionalCalcs,
    additionalMetrics,
    toShelves,
    getBarColor,
    isDataFocused,
    downsideStages,
    showPositiveLabels,
    WaterfallExtraBar,
  ]);
};

export const useFocusedData = ({
  vizId,
  toLabel = stageName => identity(stageName),
}) => {
  const dispatch = useDispatch();
  const discovery = useOpenDiscoveryPresentStateSelector({
    discoveryId: vizId,
  });
  const { focusedData = [] } = discovery;

  const buildFocusObject = useCallback(
    (d3Datum: IPipelineChangesColumnData) => {
      const label: string = toLabel(d3Datum.stageName);
      return {
        shape: LegendShapes.CIRCLE,
        label,
        info: { label },
      };
    },
    [toLabel],
  );

  const setFocusedVizData = useCallback(
    (d3Datum: IPipelineChangesColumnData) => {
      dispatch(
        Discover.setFocusedVizData(vizId, buildFocusObject(d3Datum)?.info),
      );
    },
    [dispatch, vizId, buildFocusObject],
  );

  return {
    isDataFocused: (d3Datum: IPipelineChangesColumnData) =>
      VizLegendUtils.isFocusItem(focusedData, buildFocusObject(d3Datum)?.info),
    setFocusedVizData,
    buildFocusObject,
  };
};

export const NoDataHoc: FC<INoDataHocProps> = ({
  condition = false,
  children,
}) => {
  if (condition) {
    return (
      <div css={centeredPanel()}>
        <h2>{messages.noDataWasFound}</h2>
      </div>
    );
  }
  return <>{children}</>;
};

export const useDrillLinking = ({
  vizId,
  getFieldByStageName,
  additionalCalcs = [],
  additionalMetrics = [],
  toShelves = {},
  setFocusedVizData = noop,
}: DrillLinkingProps) => {
  const viz = useOpenVizSelector({ discoveryId: vizId });
  const dispatch = useDispatch();
  const { enabled: isDrillLinkingEnabled } = useReportLinkEnabled(vizId);

  const linkToReport = useReportLinkMaybeSideDrawer({
    discoveryId: vizId,
  });

  // this filters selector is pulling values from another vizId ( the target report )
  // @NOTE using ref here because at the time of DSC-8899, there is a rules-of-react issue
  const filtersRef = useRef({});
  const filtersOptionUnparsed = get(viz?.options, VIZ_OPTION_IDS.filters, '{}');
  filtersRef.current = JSON5.parse(filtersOptionUnparsed);

  const history = useHistory();

  const fiscalCalendarInfo = useFiscalCalendarInfo({ discoveryId: vizId });

  const drillLinkIntoStage = useCallback(
    (_event: Event, d3Data: IPipelineChangesColumnData) => {
      const { stageName } = d3Data ?? {};

      if (!isFunction(getFieldByStageName) || !isString(stageName)) {
        return;
      }

      const fieldToFilter = getFieldByStageName(stageName);

      if (isNil(fieldToFilter)) {
        return;
      }

      const attributes: IPreFilterOperand[] = [
        {
          attribute: fieldToFilter,
          value: [stageName],
        },
      ];

      const drillContext: IDrillContext = {
        linkToReport: linkToReport as ILinkToReport,
        attributes,
        filters: filtersRef.current,
        calcs: additionalCalcs,
        metrics: additionalMetrics,
        toShelves,
        fiscalCalendarInfo,
      };

      if (isDrillLinkingEnabled) {
        dispatch(Discover.openReportLink(drillContext, history));
      } else if (hasDashletSuffix(linkToReport?.id)) {
        dispatch(Discover.setFocusedVizData(vizId, null));
        dispatch(Discover.setDrillContext(drillContext));
      } else {
        setFocusedVizData(d3Data);
      }
    },
    [
      getFieldByStageName,
      linkToReport,
      dispatch,
      history,
      additionalCalcs,
      additionalMetrics,
      toShelves,
      isDrillLinkingEnabled,
      vizId,
      setFocusedVizData,
      fiscalCalendarInfo,
    ],
  );

  return {
    drillLinkIntoStage,
  };
};

export const useLegend = ({ vizId, d3Data, buildFocusObject }) => {
  const dispatch = useDispatch();
  const legendData = useMemo(() => {
    const stageLegendItems: ILegendDatum[] = map(
      d3Data,
      buildFocusObject,
    ) as ILegendDatum[];
    return uniqBy(stageLegendItems, 'label');
  }, [d3Data, buildFocusObject]);
  const legendDataLabels = map(legendData, 'label');
  const legendGroupData = useVizOptionSelector({
    discoveryId: vizId,
    option: VIZ_OPTION_IDS.legendGroupMetadata,
    defaultValue: [],
  });
  const hasLegendValueChanged = useHasValueChanged({
    value: legendData,
  });

  const filteredLegendGroupData = useMemo(
    () =>
      map(legendGroupData, group => ({
        ...group,
        fields: filter(group.fields, field =>
          includes(legendDataLabels, field),
        ),
      })),
    [legendGroupData, legendDataLabels],
  );

  useEffect(() => {
    if (hasLegendValueChanged) {
      dispatch(
        Discover.updateVizLegendGroupData(vizId, filteredLegendGroupData),
      );
      dispatch(Discover.setVizLegendData(vizId, legendData));
    }
  }, [
    dispatch,
    vizId,
    legendData,
    hasLegendValueChanged,
    legendGroupData,
    legendDataLabels,
    filteredLegendGroupData,
  ]);
};

export const getD3MouseEvent = (event, ref) => {
  try {
    return pointer(event, ref ?? null);
  } catch (e) {
    console.log('Mouse event failure', e);
  }

  return null;
};

export const useMouseEvents = ({
  svgRef,
  onHover = noop,
  drillLink = noop,
}: UseMouseEventsProps) => {
  const onMouseMove = useCallback(
    (event: Event, d3Data: IPipelineChangesColumnData) => {
      const [xRelToChart = 0, yRelToChart = 0] =
        getD3MouseEvent(event, svgRef?.current) ?? [];

      onHover({
        d3Data,
        anchor: {
          posX: xRelToChart,
          posY: yRelToChart,
        },
      });
    },
    [svgRef, onHover],
  );
  const onForecastStageMouseOut = useCallback(() => {
    onHover();
  }, [onHover]);

  const onBarClick = useCallback(
    (event: Event, d3Data: IPipelineChangesColumnData) =>
      drillLink(event, d3Data),
    [drillLink],
  );

  return {
    onBarMouseEnter: onMouseMove,
    onBarMouseMove: onMouseMove,
    onBarMouseLeave: onForecastStageMouseOut,
    onBarClick,
  };
};

export const DEFAULT_PADDING_Y = 50;

const useSideDrawerHighlight = vizId => {
  const { drillContext } = useSelector((state: any) => state.discover ?? {});
  const isInSideDrawer = hasDashletSuffix(vizId);
  const isSideDrawerHighlight = isInSideDrawer && !isEmpty(drillContext);
  return { drillContext, isSideDrawerHighlight };
};

export const useRenderer = ({
  vizId,
  svgRef,
  d3Data,
  xScale,
  yScale,
  yTickValues,
  yTickFormatter,
  stageLabels,
  onBarMouseEnter = noop,
  onBarMouseMove = noop,
  onBarMouseLeave = noop,
  onBarClick = noop,
  chartYPadding,
  chartXPadding,
  setYPadding,
  upsideStages,
  xScaleDomainSelector,
  secondaryXAxisLabels,
}: UseRendererProps) => {
  const { height: chartHeight, width: chartWidth } = useContext(ChartContext);
  const {
    darkMode,
    colors: {
      MediumBorder: MediumBorderColor,
      LightFontWeight,
      Gray70,
      WaterfallExtraBar,
    },
  } = useDiscoverTheme();

  const linkToReport: ILinkToReport | object = useVizOptionSelector({
    discoveryId: vizId,
    option: VIZ_OPTION_IDS.linkToReport,
    defaultValue: {},
  });

  const availableChartWidth = chartWidth - chartXPadding * 2;

  const zeroValueBarData = filter(d3Data, filterByZeroValue);

  const showDataLabels = useVizOptionSelector({
    discoveryId: vizId,
    option: VIZ_OPTION_IDS.showDataLabels,
  });

  const canCollapseToggle: IToggle = useCustomFormatToggleSelector({
    discoveryId: vizId,
    toggleName: 'canCollapse',
    defaultValue: false,
  });

  const canCollapse = !!canCollapseToggle?.on;

  const { enabled: isDrillLinkingEnabled } = useReportLinkEnabled(vizId);
  const isDashletMode = useIsDashletMode();
  const { currentUser: { i18nPrefs = {} } = {} } = useAccount();

  const hasD3DataSignificantlyChanged = useHasValueChanged({
    value: d3Data?.length ?? 0,
  });
  const hasDrillLinkingToggleChanged = useHasValueChanged({
    value: isDrillLinkingEnabled,
  });
  const hasDrillLinkingValueChanged = useHasValueChanged({
    value: linkToReport,
  });
  const hasShowDataLabelsChanged = useHasValueChanged({
    value: showDataLabels,
  });
  const hasCanCollapseChanged = useHasValueChanged({
    value: canCollapse,
  });
  const shouldResetGraph =
    hasD3DataSignificantlyChanged ||
    hasDrillLinkingToggleChanged ||
    hasDrillLinkingValueChanged ||
    hasShowDataLabelsChanged ||
    hasCanCollapseChanged;

  const { drillContext, isSideDrawerHighlight } = useSideDrawerHighlight(vizId);
  const createGradientId = (id: string) => getGradientId('pipeline', id);

  // recreate elements and DOM listeners when data significantly changes
  useEffect(() => {
    if (shouldResetGraph) {
      const svg = select(svgRef.current);

      if (d3Data?.length > 0) {
        // remove listeners
        svg
          .selectAll('.bar.stage-data')
          .on('mouseenter', null)
          .on('mousemove', null)
          .on('mouseleave', null)
          .on('click', null);
      }

      svg.selectAll(`svg > *:not(#${TOOLTIP_ID},.axis,defs)`).remove();

      // create bar objects and bind data
      const barGroup = svg
        .insert('g', ':first-child')
        .attr('class', 'forecast-stage-data')
        .selectAll('.bar.stage-data')
        .data(d3Data)
        .enter();

      const barContainers = barGroup.insert('g');

      if (showDataLabels) {
        barContainers
          .insert('text', ':first-child')
          .attr('text-anchor', 'middle')
          .attr('class', 'bar-value-label')
          .on('mouseenter', onBarMouseEnter)
          .on('mousemove', onBarMouseMove)
          .on('mouseleave', onBarMouseLeave)
          .on(
            'click',
            (e: Event, d: IPipelineChangesColumnData) =>
              !d.isAdditionalBar && onBarClick(e, d),
          );
      }

      // create the bars overlays for white stripes
      if (isSideDrawerHighlight) {
        barContainers
          .filter((d: IPipelineChangesColumnData) => !d.isAdditionalBar)
          .insert('path', ':first-child')
          .attr('class', 'bar-value-over')
          .on('mouseenter', onBarMouseEnter)
          .on('mousemove', onBarMouseMove)
          .on('mouseleave', onBarMouseLeave)
          .on('click', onBarClick);
      }

      // create the bars and their listeners (creating listeners separately prevents overloading)
      barContainers
        .insert('path', ':first-child')
        .attr('class', 'bar stage-data')
        .on('mouseenter', onBarMouseEnter)
        .on('mousemove', onBarMouseMove)
        .on('mouseleave', onBarMouseLeave)
        .on(
          'click',
          (e: Event, d: IPipelineChangesColumnData) =>
            !d.isAdditionalBar && onBarClick(e, d),
        );

      // aria labels
      barContainers.insert('desc');

      barContainers
        .filter(filterByZeroValue)
        .insert('line', ':first-child')
        .attr('class', 'connector bar-zero-connector')
        .attr('stroke', Gray70)
        .attr('stroke-dasharray', '2,3');

      barContainers
        .filter(
          (d: IPipelineChangesColumnData) =>
            d.ordinal < d3Data.length - (last(d3Data).isAdditionalBar ? 2 : 1),
        )
        .insert('line', ':first-child')
        .attr('class', 'connector bar-adjacent-connector')
        .attr('stroke', Gray70)
        .attr('stroke-dasharray', '2');

      barContainers
        .insert('defs', ':first-child')
        .append('linearGradient')
        .attr('class', 'linear-gradient');
    }
  }, [
    shouldResetGraph,
    svgRef,
    d3Data,
    zeroValueBarData,
    onBarClick,
    onBarMouseEnter,
    onBarMouseMove,
    onBarMouseLeave,
    isDrillLinkingEnabled,
    showDataLabels,
    Gray70,
    vizId,
    isSideDrawerHighlight,
  ]);

  //  Modifies existing D3 elements (performant updates on window resize, etc)
  useEffect(() => {
    if (!isEmpty(d3Data) && !isNil(svgRef?.current)) {
      const svg = select(svgRef.current);

      // delete any existing stuff we don't need
      const axes = svg.selectAll('.axis,.background-gridlines');
      if (axes?.nodes()?.length > 0) {
        axes.remove();
      }

      // create x axis
      svg
        .insert('g', ':first-child')
        .attr('class', 'axis xAxis')
        .attr('id', 'x-axis')
        .call(axisBottom(xScale).tickValues(stageLabels))
        .attr('transform', `translate(0, ${chartHeight - chartYPadding + 1})`);

      if (!isEmpty(secondaryXAxisLabels)) {
        const secAxis = svg
          .insert('g', ':first-child')
          .attr('class', 'axis xAxis')
          .attr('id', 'x-axis-secondary')
          .attr(
            'transform',
            `translate(0, ${chartHeight - chartYPadding + 20 + 1})`,
          );
        const secAxisLabels = secAxis
          .selectAll('.label')
          .data([
            {
              key: head(stageLabels),
              value: head(secondaryXAxisLabels),
            },
            {
              key: last(d3Data).isAdditionalBar
                ? nth(stageLabels, -2)
                : last(stageLabels),
              value: last(secondaryXAxisLabels),
            },
          ])
          .enter();

        secAxisLabels
          .insert('text')
          .text(d => d.value)
          .attr('class', 'secondary-label')
          .attr('x', d => xScale(d.key) + xScale.bandwidth() / 2)
          .attr('y', 12)
          .attr('text-anchor', 'middle')
          .attr('fill', 'currentColor');
      }

      // rotate x-axis labels if they can't fit the horizontal space in the chart
      const xAxis = svgRef?.current?.querySelector('#x-axis');
      const secondaryXAxis =
        svgRef?.current?.querySelector('#x-axis-secondary');
      const xAxisLabels = xAxis?.querySelectorAll('text');
      const secXAxisLabels = secondaryXAxis?.querySelectorAll('text');
      const calculateOverlap = reduce(
        xAxisLabels,
        (acc, label: SVGTextElement) => {
          const { left, right } = label?.getBoundingClientRect();
          const { left: axisLeft, right: axisRight } =
            xAxis?.getBoundingClientRect();
          return {
            lastRight: right,
            hasOverlap:
              acc.hasOverlap ||
              left < acc.lastRight ||
              left < axisLeft ||
              right > axisRight,
          };
        },
        {
          lastRight: 0,
          hasOverlap: false,
        },
      );
      if (calculateOverlap.hasOverlap) {
        if (chartYPadding === DEFAULT_PADDING_Y) {
          setYPadding(60);
        }
        forEach(xAxisLabels, (label: SVGTextElement, index) => {
          const thisLabel = select(label);
          thisLabel
            .style('text-anchor', 'end')
            .attr('dx', '-0.8em')
            .attr('transform', 'rotate(325)');

          if (
            index === 0 ||
            index ===
              xAxisLabels.length - (last(d3Data).isAdditionalBar ? 2 : 1)
          ) {
            const label =
              index === 0 ? head(secXAxisLabels) : last(secXAxisLabels);
            if (!isNil(label)) {
              const secLabelText = select(label);
              if (!isEmpty(secLabelText.text())) {
                thisLabel.text(secLabelText.text());
              }
            }
          }
        });
        forEach(secXAxisLabels, (label: SVGTextElement) => {
          select(label).remove();
        });
      } else if (chartYPadding !== DEFAULT_PADDING_Y) {
        setYPadding(DEFAULT_PADDING_Y);
      }

      // create y axis
      svg
        .insert('g', ':first-child')
        .attr('class', 'axis yAxis gridlines')
        .attr('id', 'y-axis-gridlines')
        .call(
          axisLeft(yScale)
            .tickValues(yTickValues)
            .tickFormat(
              (d, i) =>
                yTickValues.map(v => yTickFormatter.formatSmall(v, i18nPrefs))[
                  i
                ],
            ),
        )
        .attr('transform', `translate(${chartXPadding}, 0)`);

      const yGridLines = axisLeft(yScale)
        .tickValues(yTickValues)
        .tickFormat(constant(''))
        .tickSizeInner(-chartWidth + chartXPadding + DEFAULT_PADDING_Y);

      svg
        .insert('g', ':first-child')
        .attr('class', 'background-gridlines')
        .attr('transform', `translate(${chartXPadding}, 0)`)
        .call(yGridLines);

      svg
        .selectAll('g.background-gridlines line')
        .attr('stroke', MediumBorderColor);

      svg
        .selectAll('text.bar-value-label')
        .attr('font-size', 14)
        .attr('font-weight', LightFontWeight);

      svg
        .selectAll('g.axis .tick text, g.axis text.secondary-label')
        .attr('font-size', 12)
        .attr('font-weight', LightFontWeight);

      // hide the vertical axis edge
      svg
        .selectAll('g.yAxis .domain,g.background-gridlines .domain')
        .attr('stroke-width', 0);
      svg.selectAll('g.yAxis line').attr('stroke-width', 0);

      svg
        .selectAll('linearGradient')
        .data(d3Data)
        .attr(
          'id',
          (d: IPipelineChangesColumnData) => `${createGradientId(d.fill)}`,
        )
        .attr('gradientTransform', 'rotate(90)')
        .selectAll('stop')
        .data((d: IPipelineChangesColumnData) => {
          const { adjustedDerivativeColor, adjustedBaseColor } =
            getAdjustedColors({
              isDarkMode: darkMode,
              baseColor: d.fill,
            });
          return [
            { offset: '0%', stopColor: adjustedBaseColor },
            { offset: '100%', stopColor: adjustedDerivativeColor },
          ];
        })
        .join(
          enter =>
            enter
              .append('stop')
              .attr('offset', d => d.offset)
              .attr('stop-color', d => d.stopColor),
          update =>
            update
              .attr('offset', d => d.offset)
              .attr('stop-color', d => d.stopColor),
        );

      // create stage elements
      const bars = svg.selectAll('.bar.stage-data');
      bars.data(d3Data);
      svg.selectAll('text.bar-value-label').data(d3Data);
      svg.selectAll('.forecast-stage-data desc').data(d3Data);
      svg
        .selectAll('line.bar-adjacent-connector')
        .data(d3Data.filter(d => !d.isAdditionalBar));
      svg.selectAll('line.bar-zero-connector').data(zeroValueBarData);

      svg
        .selectAll('.forecast-stage-data desc')
        .text((d: IPipelineChangesColumnData) => !d.isAdditionalBar && d.label);

      bars
        .attr('d', (d: IPipelineChangesColumnData) =>
          topRoundedRect(d.x, d.y, d.width, d.height),
        )
        .attr(
          'fill',
          (d: IPipelineChangesColumnData) =>
            `url(#${createGradientId(d.fill)})`,
        )
        .attr(
          'stroke',
          (d: IPipelineChangesColumnData) =>
            `url(#${createGradientId(d.fill)})`,
        )
        .attr('opacity', (d: IPipelineChangesColumnData) =>
          d.dim ? '0.16' : '1',
        );

      if (isSideDrawerHighlight) {
        const barOverlays = svg.selectAll('.bar-value-over');
        barOverlays.data(d3Data);
        const selectDrillContextProps = drillContext => {
          const rAttributes = get(drillContext, 'attributes', []);
          const attrs = map(rAttributes, ({ attribute, value }) => [
            attribute?.name,
            value,
          ]);
          return attrs;
        };

        barOverlays
          .attr('d', (d: IPipelineChangesColumnData) =>
            topRoundedRect(d.x, d.y, d.width, d.height),
          )
          .attr('fill', (d: IPipelineChangesColumnData) => {
            const isHighlighted =
              isSideDrawerHighlight &&
              isEqual(
                selectDrillContextProps(drillContext),
                selectDrillContextProps(d?.drillContext),
              );
            return isHighlighted ? whiteStripesFill : d.fill;
          })
          .attr('stroke', (d: IPipelineChangesColumnData) => d.fill)
          .attr('opacity', (d: IPipelineChangesColumnData) =>
            d.dim ? '0.16' : '1',
          );
      }

      const barLabels = svg
        .selectAll('text.bar-value-label')
        .attr('x', (d: IPipelineChangesColumnData) => d.labelPosX)
        .attr('y', (d: IPipelineChangesColumnData) => {
          if (d.height >= 14) {
            return d.y + 4 + d.height / 2;
          }
          return d.y - 5;
        })
        .attr('fill', (d: IPipelineChangesColumnData) => {
          if (d.height >= 14) {
            return Viz.getContrastColor(color(d.fill) as any);
          }
          return 'currentColor';
        })
        .attr('width', '200px');
      const textLabels = barLabels.text(null);

      const multiLineLabels =
        map(d3Data, ({ label, width }) => Viz.multiLineTSpans(label, width)) ??
        [];
      const numTspans = max(map(multiLineLabels, 'label.length'));

      if (numTspans > 1 && !(textLabels.select('tspan').size() > 0)) {
        for (let i = 0; i < numTspans; i++) {
          textLabels.append('tspan');
        }
        for (let i = 0; i < multiLineLabels?.length; i++) {
          for (let j = 0; j < numTspans; j++) {
            const labelArrLength = multiLineLabels[i]?.label?.length;
            svg
              .select(`text.bar-value-label:nth-of-type(${i + 1})`)
              .attr(
                'y',
                (d: IPipelineChangesColumnData) => d.y - labelArrLength * 12,
              );

            const x =
              toNumber(d3Data[i]?.labelPosX) +
              toNumber(multiLineLabels[i]?.label[j]?.x);
            const dy = multiLineLabels[i]?.label[j]?.dy;
            const text = multiLineLabels[i]?.label[j]?.text;
            if (isNumber(x) && isNumber(dy) && isString(text)) {
              svg
                .select(
                  `text.bar-value-label:nth-of-type(${
                    i + 1
                  }) tspan:nth-of-type(${j + 1})`,
                )
                .text(text)
                .attr('x', x)
                .attr('dy', `${dy}em`);
            }
          }
        }
      } else {
        barLabels.text((d: IPipelineChangesColumnData) => d.label);
      }

      const getAdjacentConnectorYPos = (d: IPipelineChangesColumnData) =>
        includes(upsideStages, d.stageName) ? d.y : d.y + d.height;
      svg
        .selectAll('line.bar-adjacent-connector')
        .attr('x1', (d: IPipelineChangesColumnData) => d.adjacentConnectorLeft)
        .attr('y1', getAdjacentConnectorYPos)
        .attr('x2', (d: IPipelineChangesColumnData) => d.adjacentConnectorRight)
        .attr('y2', getAdjacentConnectorYPos);

      svg
        .selectAll('line.bar-zero-connector')
        .attr('x1', (d: IPipelineChangesColumnData) => d.x)
        .attr('y1', (d: IPipelineChangesColumnData) => d.y)
        .attr('x2', (d: IPipelineChangesColumnData) => d.x + d.width)
        .attr('y2', (d: IPipelineChangesColumnData) => d.y);
    }
  }, [
    xScale,
    yScale,
    xScaleDomainSelector,
    i18nPrefs,
    d3Data,
    zeroValueBarData,
    svgRef,
    chartHeight,
    availableChartWidth,
    yTickValues,
    yTickFormatter,
    stageLabels,
    chartXPadding,
    chartYPadding,
    setYPadding,
    chartWidth,
    upsideStages,
    isDashletMode,
    MediumBorderColor,
    LightFontWeight,
    darkMode,
    secondaryXAxisLabels,
    vizId,
    drillContext,
    isSideDrawerHighlight,
    WaterfallExtraBar,
  ]);
};
